import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = anonymous
  const [token, setToken] = useState(() => localStorage.getItem("vt_token") || "");

  useEffect(() => {
    if (!token) { setUser(null); return; }
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    (async () => {
      // Try admin /auth/me first, fallback to agency /agency/me
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch { /* not admin */ }
      try {
        const { data } = await api.get("/agency/me");
        setUser(data);
      } catch {
        setUser(null);
      }
    })();
  }, [token]);

  const login = async (email, password, totp = "") => {
    try {
      const { data } = await api.post("/auth/login", { email, password, totp });
      localStorage.setItem("vt_token", data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "2FA_REQUIRED") return { ok: false, requires2fa: true };
      return { ok: false, error: formatApiError(detail) || e.message };
    }
  };

  const agencyRegister = async (payload) => {
    try {
      const { data } = await api.post("/auth/agency-register", payload);
      localStorage.setItem("vt_token", data.token);
      api.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      setToken(data.token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("vt_token");
    delete api.defaults.headers.common.Authorization;
    setToken("");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, agencyRegister }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
